import React, { useEffect, useRef } from 'react';
import { Landmark, Calculator, Wallet, CalendarClock } from 'lucide-react';
import { computeFinance, inr } from '../../lib/financeMath';
import { readCompanyBanks } from '../../lib/financeCatalogs';
import { resolveBankLabel } from '../../lib/paymentVia';
import { PaymentViaFields } from '../../components/common/PaymentViaFields';
import { AmountInput } from '../../components/common/AmountInput';
import { parseAmountInput } from '../../lib/formatInr';

interface FinancialInformationProps {
  formData: any;
  setFormData: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export function FinancialInformation({
  formData,
  setFormData,
  onNext,
  onBack,
}: FinancialInformationProps) {
  const emiManual = useRef(Boolean(String(formData.emiAmount ?? '').trim()));
  const calc = computeFinance(formData);

  useEffect(() => {
    setFormData((prev: any) => {
      const c = computeFinance(prev);
      const next = { ...prev };
      next.amountBalance = c.balance ? String(c.balance) : '';
      next.totalInterest = c.totalInterest ? String(c.totalInterest) : '';
      next.checkEmiAmount = c.emi ? String(c.emi) : '';
      if (!emiManual.current) next.emiAmount = c.emi ? String(c.emi) : '';
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.totalAmount, formData.amount, formData.outPayment, formData.interestPercentage, formData.emiPeriod]);

  const set = (patch: Record<string, unknown>) => setFormData({ ...formData, ...patch });

  const syncDisbursement = (
    via: string,
    bankId: string,
    paidBy: string,
    cashAmount: string,
    bankAmount: string
  ) => {
    const banks = readCompanyBanks();
    const label = resolveBankLabel(banks, bankId);
    set({
      disbursementVia: via,
      disbursementBankId: bankId,
      disbursementBankLabel: label,
      disbursementPaidBy: paidBy,
      disbursementCashAmount: cashAmount,
      disbursementBankAmount: bankAmount,
      bankTransaction: via === 'online' || via === 'split' ? 'yes' : via === 'cash' ? 'no' : '',
      bank: via === 'online' || via === 'split' ? label || formData.bank : '',
      chqNoDescription: paidBy || formData.chqNoDescription,
    });
  };

  const useCalculatedEmi = () => {
    emiManual.current = false;
    set({ emiAmount: calc.emi ? String(calc.emi) : '' });
  };

  return (
    <div className="app-card overflow-hidden">
      <div className="app-card-inner">
        <div className="app-section-head">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center border border-emerald-800 bg-emerald-800 text-white">
              <Landmark className="h-5 w-5" />
            </div>
            <div>
              <h2 className="app-section-title">Step 2 — Finance</h2>
              <p className="app-section-desc">Facility amounts, disbursement channel, and repayment plan.</p>
            </div>
          </div>
        </div>

        <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-800">
              <Wallet className="h-4 w-4" />
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400">1 · Facility amounts</h3>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6">
              <AmountInput
                id="fin-total-amount"
                label="Total amount (principal)"
                value={formData.totalAmount}
                onChange={(v) => set({ totalAmount: v })}
                placeholder="Agreement / vehicle value"
              />
              <AmountInput
                id="fin-out-payment"
                label="Out payment"
                value={formData.outPayment}
                onChange={(v) => set({ outPayment: v })}
                placeholder="Down / out payment"
              />
              <div>
                <label className="app-label">OP interest</label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="app-field"
                  value={formData.opInterest}
                  onChange={(e) => set({ opInterest: e.target.value })}
                  placeholder="Defaults from Settings → Finance"
                />
              </div>
              <AmountInput
                id="fin-amount-optional"
                label="Amount (optional)"
                value={formData.amount}
                onChange={(v) => set({ amount: v })}
                placeholder="Used if total amount is blank"
              />
              <div>
                <label className="app-label">Agreement date</label>
                <input
                  type="date"
                  className="app-field"
                  value={formData.agreementDate || ''}
                  onChange={(e) => set({ agreementDate: e.target.value })}
                />
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">Facility start / books date for EMI schedule. Defaults to Working date in the navbar.</p>
              </div>
              <div>
                <label className="app-label">Agreement</label>
                <input
                  type="text"
                  className="app-field"
                  value={formData.agreement}
                  placeholder="Agreement reference"
                  onChange={(e) => set({ agreement: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label">Agreement finance</label>
                <select
                  className="app-field"
                  value={formData.agreementFinance}
                  onChange={(e) => set({ agreementFinance: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-6">
            <PaymentViaFields
              mode="disbursement"
              value={{
                paymentVia: formData.disbursementVia || '',
                companyBankId: formData.disbursementBankId || '',
                paidBy: formData.disbursementPaidBy || '',
                cashAmount: formData.disbursementCashAmount || '',
                bankAmount: formData.disbursementBankAmount || '',
              }}
              onChange={(v) =>
                syncDisbursement(v.paymentVia, v.companyBankId, v.paidBy, v.cashAmount, v.bankAmount)
              }
              totalAmount={Math.max(
                0,
                parseFloat(String(formData.totalAmount || formData.amount || '').replace(/,/g, '')) || 0
              )}
            />
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-6">
            <div className="flex items-center gap-2 text-emerald-800">
              <CalendarClock className="h-4 w-4" />
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400">3 · Repayment plan</h3>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6">
              <div>
                <label className="app-label">Interest (₹ per ₹100 principal / month)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min="0"
                  className="app-field"
                  value={formData.interestPercentage}
                  onChange={(e) => set({ interestPercentage: e.target.value })}
                  placeholder="e.g. 1"
                />
              </div>
              <div>
                <label className="app-label">EMI period (months)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  className="app-field"
                  value={formData.emiPeriod}
                  onChange={(e) => set({ emiPeriod: e.target.value })}
                  placeholder="Number of months"
                />
              </div>
            </div>

            {calc.balance > 0 && calc.rupeesPerHundred > 0 ? (
              <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900/80">Interest on financed balance</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <CalcStat label="Interest per month" value={inr(calc.interestPerMonth)} hint={`balance × (${calc.rupeesPerHundred} ÷ 100)`} />
                  <CalcStat label="Total interest" value={inr(calc.totalInterest)} hint={`${inr(calc.interestPerMonth)} × ${calc.months || 0} months`} />
                </div>
              </div>
            ) : null}

            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-emerald-700" />
                <p className="text-sm font-semibold text-emerald-900">Auto-calculated EMI</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <CalcStat label="Balance financed" value={inr(calc.balance)} hint="Total − Out payment" />
                <CalcStat label="Total interest" value={inr(calc.totalInterest)} hint={`balance × (${calc.rupeesPerHundred || 0} ÷ 100) × ${calc.months || 0} mo`} />
                <CalcStat label="Total payable" value={inr(calc.totalPayable)} hint="Balance + interest" />
                <CalcStat label="Monthly EMI" value={inr(calc.emi)} hint={`over ${calc.months || 0} mo`} highlight />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6">
              <div>
                <label className="app-label">Check EMI amount (auto)</label>
                <input type="text" className="app-field bg-slate-100 tabular-nums" value={formData.checkEmiAmount ? inr(parseAmountInput(formData.checkEmiAmount)) : ''} readOnly />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="app-label mb-0">EMI amount</label>
                  {calc.emi > 0 && String(formData.emiAmount) !== String(calc.emi) ? (
                    <button type="button" onClick={useCalculatedEmi} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900">
                      Use calculated ({inr(calc.emi)})
                    </button>
                  ) : null}
                </div>
                <AmountInput
                  id="fin-emi-amount"
                  value={formData.emiAmount}
                  onChange={(v) => {
                    emiManual.current = true;
                    set({ emiAmount: v });
                  }}
                  placeholder="Auto-filled — edit to override"
                />
              </div>
            </div>
          </section>
        </form>

        <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5">
          <button type="button" onClick={onBack} className="app-btn-secondary">
            Back
          </button>
          <button type="button" onClick={onNext} className="app-btn-primary">
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function CalcStat({
  label,
  value,
  hint,
  highlight = false,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 ${
        highlight
          ? 'border-emerald-300 bg-white shadow-sm dark:border-emerald-700/50 dark:bg-zinc-800/90'
          : 'border-emerald-100 bg-white/70 dark:border-emerald-900/40 dark:bg-zinc-800/70'
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400">{label}</p>
      <p className={`mt-0.5 font-bold ${highlight ? 'text-base text-emerald-800 dark:text-emerald-300' : 'text-sm text-slate-900 dark:text-zinc-100'}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-[10px] text-slate-400 dark:text-zinc-500">{hint}</p> : null}
    </div>
  );
}
